import React, { useEffect, useState, useMemo } from "react";
import {
  Legend,
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { GasMonitor, Gas } from "../../api";
import { useGlobal } from "../../global/user";
import { Modal, Button, Switch, Select, Row, Col, Card, Checkbox, Space, DatePicker, Form, } from "antd";
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const { Option } = Select;
const { RangePicker } = DatePicker;

const DEFAULT_COLORS = [
  "#ff4d4f", "#1890ff", "#52c41a", "#722ed1", 
  "#fa8c16", "#eb2f96", "#13c2c2", "#a0d911"
];

const SENSOR_MAPPING = {
  sensor1: { dataKey: 'a_val', defaultName: 'temp' },
  sensor2: { dataKey: 'b_val', defaultName: 'hum' },
  sensor3: { dataKey: 'c_val', defaultName: 'O2' },
  sensor4: { dataKey: 'd_val', defaultName: 'CO' },
  sensor5: { dataKey: 'e_val', defaultName: 'LEL' },
  sensor6: { dataKey: 'f_val', defaultName: 'H2S' },
  sensor7: { dataKey: 'g_val', defaultName: 'CH4' },
  sensor8: { dataKey: 'h_val', defaultName: 'CO2' },
  sensor9: { dataKey: 'heat_index', defaultName: '熱指數' }
};

// 熱危害分級虛線：依職安署熱指數表三的熱指數分區（= 90/105/130°F）
// 後端 gasmonitor_mqtt.py HEAT_LEVEL_THRESHOLDS 使用同一組數值，修改時兩邊須同步
const HEAT_SENSOR_KEY = 'sensor9';
const HEAT_LEVEL_LINES = [
  { level: 2, value: 32.2, label: '第二級 格外注意 32.2', color: '#d4b106' },
  { level: 3, value: 40.6, label: '第三級 危險 40.6', color: '#fa8c16' },
  { level: 4, value: 54.4, label: '第四級 極度危險 54.4', color: '#f5222d' },
];
const HEAT_LEVEL_NAMES = {
  0: '未達警戒值',
  1: '第一級 注意',
  2: '第二級 格外注意',
  3: '第三級 危險',
  4: '第四級 極度危險',
};
const HEAT_TABLE_MIN = 26.7;

// 熱指數 → 等級，與後端 get_heat_risk_level_by_index 相同規則
const getHeatLevel = (heatIndex) => {
  const hi = Number(heatIndex);
  if (heatIndex === null || heatIndex === undefined || isNaN(hi)) return null;
  if (hi < HEAT_TABLE_MIN) return 0;
  for (let i = HEAT_LEVEL_LINES.length - 1; i >= 0; i--) {
    if (hi >= HEAT_LEVEL_LINES[i].value) return HEAT_LEVEL_LINES[i].level;
  }
  return 1;
};

// 上限／下限各自是否啟用：舊資料沒有旗標時視為啟用；雨量計沒有下限
const isUpperEnabled = (config) =>
  !!config?.bounds && config.bounds.upperEnabled !== false && config.bounds.upperBound !== null && config.bounds.upperBound !== undefined;
const isLowerEnabled = (config) =>
  !!config?.bounds && !config.isRain && config.bounds.lowerEnabled !== false && config.bounds.lowerBound !== null && config.bounds.lowerBound !== undefined;

// 熱指數分級虛線是否顯示
const isHeatLinesEnabled = (config) => config?.levelLines !== false;

// 依感測器設定產生參考虛線；回傳陣列（Recharts 不支援 Fragment 包裹子元件）
const renderReferenceLines = (sensorKey, config, withLabel = true) => {
  if (sensorKey === HEAT_SENSOR_KEY) {
    if (!isHeatLinesEnabled(config)) return [];
    return HEAT_LEVEL_LINES.map(line => (
      <ReferenceLine
        key={`heat-${line.level}`}
        y={line.value}
        stroke={line.color}
        strokeDasharray="4 4"
        label={withLabel ? { value: line.label, position: 'insideTopRight', fill: line.color, fontSize: 11 } : undefined}
      />
    ));
  }

  const lines = [];
  if (isLowerEnabled(config)) {
    const { lowerBound, tolerance } = config.bounds;
    lines.push(
      <ReferenceLine key="lb" y={lowerBound} label={withLabel ? "下限" : undefined} stroke="red" strokeDasharray="2 2" />
    );
    if (withLabel && tolerance > 0) {
      lines.push(
        <ReferenceLine
          key="tol"
          y={lowerBound - tolerance}
          label={`下限放寬 (-${tolerance})`}
          stroke="orange"
          strokeDasharray="4 4"
        />
      );
    }
  }
  if (isUpperEnabled(config)) {
    lines.push(
      <ReferenceLine key="ub" y={config.bounds.upperBound} label={withLabel ? "上限" : undefined} stroke="red" strokeDasharray="2 2" />
    );
  }
  return lines;
};

// Tooltip 狀態文字：熱指數顯示等級，其他感測器在上下限啟用時才判斷超限
const getValueStatus = (sensorKey, config, value) => {
  if (value === null || value === undefined) return "";
  if (sensorKey === HEAT_SENSOR_KEY) {
    const level = getHeatLevel(value);
    return level === null ? "" : ` (${HEAT_LEVEL_NAMES[level]})`;
  }
  if (isUpperEnabled(config) && value > config.bounds.upperBound) return " (超出上限)";
  if (isLowerEnabled(config) && value < config.bounds.lowerBound) return " (低於下限)";
  return "";
};

const linearZigzagInterpolation = (startValue, endValue, steps, zigzagFactor = 0.05) => {
  if (steps <= 0) return [];
  
  const interpolatedData = [];
  const baseStep = (endValue - startValue) / (steps + 1);
  
  for (let i = 1; i <= steps; i++) {
    const linearValue = startValue + (baseStep * i);
    
    const maxOffset = Math.abs(linearValue) * zigzagFactor;
    const randomOffset = (Math.random() - 0.5) * 2 * maxOffset;
    const zigzagValue = linearValue + randomOffset;
    
    interpolatedData.push(zigzagValue);
  }
  
  return interpolatedData;
};

const hasDataGap = (prevTime, nextTime) => {
  const timeDiff = nextTime - prevTime;
  const oneHourInMs = 60 * 60 * 1000; // 1小時
  return timeDiff > oneHourInMs;
};

const generateTimeGapPoints = (prevTime, nextTime, prevValue, nextValue) => {
  const tenMinutesInMs = 10 * 60 * 1000; // 10分鐘
  const points = [];
  
  let currentTime = prevTime + tenMinutesInMs;
  let stepCount = 0;
  
  while (currentTime < nextTime) {
    stepCount++;
    currentTime += tenMinutesInMs;
  }

  // 使用鋸齒插值生成數值
  const interpolatedValues = linearZigzagInterpolation(prevValue, nextValue, stepCount, 0.03);
  
  currentTime = prevTime + tenMinutesInMs;
  interpolatedValues.forEach((value, index) => {
    points.push({
      time: currentTime,
      value: parseFloat(value.toFixed(2)),
      isInterpolated: true
    });
    currentTime += tenMinutesInMs;
  });
  
  return points;
};

const needsInterpolation = (value, bounds, enableInterpolation) => {
  // 如果沒有開啟補點功能，只處理null值
  if (!enableInterpolation) {
    return value === null || value === undefined || isNaN(value);
  }
  
  // 開啟補點時，只處理null值/undefined/NaN
  // 注意：超出邊界的值是真實數據，不應被插值替換，否則會導致圖表掩蓋警報觸發點
  if (value === null || value === undefined || isNaN(value)) {
    return true;
  }
  
  return false;
};

const processDataWithInterpolation = (rawData, sensorConfigs, enableInterpolation = true) => {
  if (!rawData || rawData.length === 0) {
    return rawData;
  }

  let processedData = [...rawData];
  
  Object.keys(SENSOR_MAPPING).forEach(sensorKey => {
    const config = sensorConfigs[sensorKey];
    if (!config || !config.name || !config.name.trim()) {
      return;
    }

    // 雨量感測器：後端回傳的是差分後的時段雨量，插值/鋸齒合成會捏造降雨資料，一律跳過
    if (config.isRain) {
      return;
    }
    
    const dataKey = SENSOR_MAPPING[sensorKey].dataKey;
    const bounds = config.bounds;
    
    if (!enableInterpolation) {
      // 補點關閉：保留原始真實值，不做任何夾值處理
      // 注意：不應將超標值壓到邊界值，否則會掩蓋異常數據點
      for (let i = 0; i < processedData.length; i++) {
        const originalValue = processedData[i][dataKey];
        
        // 為每個數據點添加原始值備份（保持與其他邏輯的相容性）
        if (!processedData[i]._originalValues) {
          processedData[i]._originalValues = {};
        }
        processedData[i]._originalValues[dataKey] = originalValue;
      }
    } else {
      // 補點開啟：執行插值處理
      const interpolationPoints = [];
      const timeGapPoints = [];
      
      for (let i = 0; i < processedData.length; i++) {
        const currentValue = processedData[i][dataKey];
        const currentTime = processedData[i].time;
        
        // 檢查當前值是否需要插值
        if (needsInterpolation(currentValue, bounds, enableInterpolation)) {
          let prevIndex = i - 1;
          let nextIndex = i + 1;
          
          // 找到前一個有效值
          while (prevIndex >= 0) {
            const prevValue = processedData[prevIndex][dataKey];
            if (!needsInterpolation(prevValue, bounds, true)) {
              break;
            }
            prevIndex--;
          }

          // 找到下一個有效值
          while (nextIndex < processedData.length) {
            const nextValue = processedData[nextIndex][dataKey];
            if (!needsInterpolation(nextValue, bounds, true)) {
              break;
            }
            nextIndex++;
          }
          
          if (prevIndex >= 0 && nextIndex < processedData.length) {
            const prevValue = processedData[prevIndex][dataKey];
            const nextValue = processedData[nextIndex][dataKey];
            
            interpolationPoints.push({
              index: i,
              prevValue: prevValue,
              nextValue: nextValue
            });
          }
        }
        
        // 檢查時間間隔（數據中斷超過1小時）
        if (i > 0) {
          const prevTime = processedData[i-1].time;
          const prevValue = processedData[i-1][dataKey];
          
          if (hasDataGap(prevTime, currentTime) && 
              !needsInterpolation(prevValue, bounds, true) && 
              !needsInterpolation(currentValue, bounds, true)) {
            
            const gapPrevValue = prevValue;
            const gapNextValue = currentValue;
            
            const gapPoints = generateTimeGapPoints(prevTime, currentTime, gapPrevValue, gapNextValue);
            timeGapPoints.push({
              insertAfterIndex: i - 1,
              points: gapPoints.map(point => ({
                ...processedData[i-1],
                time: point.time,
                [dataKey]: point.value
              }))
            });
          }
        }
      }

      // 應用普通插值（僅用於補 null 點，不限制邊界）
      interpolationPoints.forEach(point => {
        const { index, prevValue, nextValue } = point;
        const interpolatedValues = linearZigzagInterpolation(prevValue, nextValue, 1, 0.03);
        
        if (interpolatedValues.length > 0) {
          const interpolatedValue = parseFloat(interpolatedValues[0].toFixed(2));
          processedData[index][dataKey] = interpolatedValue;
        }
      });
      
      // 插入時間間隔補點
      timeGapPoints.reverse().forEach(gapInfo => {
        const { insertAfterIndex, points } = gapInfo;
        processedData.splice(insertAfterIndex + 1, 0, ...points);
      });
    }
  });
  
  // 重新排序數據（按時間）
  processedData.sort((a, b) => a.time - b.time);
  
  return processedData;
};

const initialState = {
  data: [],
  left: "dataMin", 
  right: "dataMax",
  animation: true,
};

/**
 * 雨量整點分桶：後端回傳的每筆值為「與上一筆之間的時段雨量」，
 * 依該筆時間戳所在的整點小時加總（差分歸屬於後一筆所在小時）。
 * 回傳 [{ time, label, value, inProgress }]，inProgress 標記進行中的當前小時。
 */
const aggregateRainHourly = (data, dataKey) => {
  const buckets = new Map();

  (data || []).forEach(row => {
    const value = row[dataKey];
    if (value === null || value === undefined || isNaN(value)) return;

    const d = new Date(row.time);
    const hourStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours()).getTime();
    buckets.set(hourStart, (buckets.get(hourStart) || 0) + Number(value));
  });

  const now = new Date();
  const currentHourStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()).getTime();

  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([hourStart, total]) => {
      const d = new Date(hourStart);
      const pad = (n) => String(n).padStart(2, '0');
      return {
        time: hourStart,
        label: `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:00`,
        value: parseFloat(total.toFixed(2)),
        inProgress: hourStart === currentHourStart
      };
    });
};

export default function GasChart({
  id,
  visible,
  setVisible,
  sensorConfigs = {},
  onConfigChange = () => {}
}) {
  const { user } = useGlobal();
  const [state, setState] = useState(initialState);
  const [refLeft, setLeft] = useState("");
  const [refRight, setRight] = useState("");
  const [activeSensor, setActiveSensor] = useState("");
  const [viewMode, setViewMode] = useState("single");
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHeatRisk, setShowHeatRisk] = useState(false);
  
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return [today, endOfToday];
  });
  const [enableInterpolation, setEnableInterpolation] = useState(true);
  const [interpolationStats, setInterpolationStats] = useState({});
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [form] = Form.useForm();
  const { data, left, right } = state;

  const getActiveSensors = () => {
    return Object.keys(SENSOR_MAPPING)
      .map(key => {
        const config = sensorConfigs[key];
        const mapping = SENSOR_MAPPING[key];

        if (key === 'sensor9') {
          if (!showHeatRisk || !config?.name || !config.name.trim()) {
            return null;
          }
        }
        
        const displayName = (config?.name && config.name.trim()) || 
                          (config?.mqttField && config.mqttField.trim()) || 
                          '';
        
        if (!displayName) {
          return null;
        }
        
        return {
          key,
          name: displayName,
          color: DEFAULT_COLORS[parseInt(key.replace('sensor', '')) - 1],
          dataKey: mapping.dataKey,
          bounds: config.bounds || { upperBound: 100, lowerBound: 0, tolerance: 1 },
          isRain: !!config.isRain
        };
      })
      .filter(Boolean);
  };


  useEffect(() => {
    if (visible && data.length > 0) {
      setState(prevState => ({ ...prevState }));
    }

    if (sensorConfigs['sensor9'] && sensorConfigs['sensor9'].name) {
      setShowHeatRisk(true);
    } else {
      setShowHeatRisk(false);
    }
  }, [sensorConfigs, visible]);
    
  useEffect(() => {
    const activeSensors = getActiveSensors();
    if (activeSensors.length > 0) {
      setActiveSensor(activeSensors[0].key);
      setSelectedSensors([activeSensors[0].key]);
    }
  }, [sensorConfigs]);

  useEffect(() => {
    if (!visible || dateRange.length !== 2) return;
    loadChartData();
  }, [visible, dateRange, enableInterpolation]);

  const loadChartData = async () => {
    if (!dateRange || dateRange.length !== 2) return;
    
    setLoading(true);
    setInterpolationStats({});
    
    try {
      const [startDate, endDate] = dateRange;

      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      const startDateStr = formatLocalDateTime(startDate);
      const endDateStr = formatLocalDateTime(endDate);

      console.log('發送 API 請求:', {
        deviceId: id,
        startDate: startDateStr,
        endDate: endDateStr,
        originalDates: dateRange
      });

      const chartDataRes = await Gas.getChartDataByDateRange(
        id, 
        startDateStr, 
        endDateStr
      );
      
      if (chartDataRes.code === 200) {
        let rawData = chartDataRes.data.map(row => {
          const time = new Date(row.time);
          return {
            ...row,
            time: time.getTime(),
          };
        });

        console.log('原始數據詳細:', {
          count: rawData.length,
          firstRecord: rawData[0],
          lastRecord: rawData[rawData.length - 1],
        });

        const calibratedData = rawData; // 校正已由後端 MQTT 處理器套用，前端不重複套用
        const processedData = processDataWithInterpolation(calibratedData, sensorConfigs, enableInterpolation);

        console.log('校正和處理後數據詳細:', {
          count: processedData.length,
          firstRecord: processedData[0],
          lastRecord: processedData[processedData.length - 1]
        });

        // 檢查當前選中感測器的數據
        if (activeSensor) {
          const dataKey = SENSOR_MAPPING[activeSensor].dataKey;
          const validData = processedData.filter(row => 
            row[dataKey] !== null && row[dataKey] !== undefined && !isNaN(row[dataKey])
          );
          
          console.log(`感測器 ${activeSensor} 數據檢查:`, {
            dataKey: dataKey,
            totalPoints: processedData.length,
            validPoints: validData.length,
            sampleValues: processedData.slice(0, 5).map(row => ({
              time: new Date(row.time).toLocaleString(),
              value: row[dataKey]
            })),
            sensorConfig: sensorConfigs[activeSensor]
          });
        }

        if (enableInterpolation) {
          const stats = {};
          Object.keys(SENSOR_MAPPING).forEach(sensorKey => {
            const config = sensorConfigs[sensorKey];
            if (config && config.name && config.name.trim() && !config.isRain) {
              const dataKey = SENSOR_MAPPING[sensorKey].dataKey;
              let interpolatedCount = 0;
              let timeGapCount = 0;
              
              for (let i = 0; i < rawData.length; i++) {
                const rawValue = rawData[i][dataKey];
                const processedValue = processedData[i] ? processedData[i][dataKey] : null;
                if (rawValue !== processedValue) {
                  interpolatedCount++;
                }
              }
              
              timeGapCount = processedData.length - rawData.length;
              
              stats[sensorKey] = {
                total: processedData.length,
                original: rawData.length,
                interpolated: interpolatedCount,
                timeGapFilled: timeGapCount,
                percentage: rawData.length > 0 ? ((interpolatedCount / rawData.length) * 100).toFixed(1) : 0
              };
            }
          });
          setInterpolationStats(stats);
        }
        
        // 強制重置圖表狀態並設置新數據
        setState({
          data: processedData,
          left: "dataMin",
          right: "dataMax", 
          animation: true
        });

        console.log('圖表狀態已更新:', {
          dataCount: processedData.length,
          chartState: {
            left: "dataMin",
            right: "dataMax",
            animation: true
          }
        });

      } else {
        console.error("獲取資料失敗:", chartDataRes.message);
        // 清空數據
        setState({
          data: [],
          left: "dataMin",
          right: "dataMax",
          animation: true
        });
      }
    } catch (error) {
      console.error("載入圖表資料失敗:", error);
      // 發生錯誤時清空數據
      setState({
        data: [],
        left: "dataMin", 
        right: "dataMax",
        animation: true
      });
    } finally {
      setLoading(false);
    }
  };

  // CSV 下載函數
  const exportToCSV = () => {
    if (!data || data.length === 0) {
      Modal.warning({ title: '無可下載的數據', content: '請先載入圖表數據' });
      return;
    }

    // 構建表頭：空白列 + 感測器名稱（雨量欄位標註為時段雨量）
    const headers = ['', ...activeSensors.map(s => s.isRain ? `${s.name}(時段雨量mm)` : s.name)];
    
    // 構建數據行
    const rows = data.map(item => {
      const timeStr = new Date(item.time).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(/\//g, '/');
      
      const values = activeSensors.map(s => {
        const value = item[SENSOR_MAPPING[s.key].dataKey];
        return value === null || value === undefined || isNaN(value) ? '--' : value;
      });
      
      return [timeStr, ...values];
    });
    
    // 組合 CSV 內容
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');
      
    // 下載文件
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `氣體監測數據_${id}_${new Date().toLocaleDateString('zh-TW').replace(/\//g, '')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    
    setShowDownloadModal(false);
  };

  const exportToPDF = async () => {
    if (!data || data.length === 0) {
      Modal.warning({ title: '無可下載的數據', content: '請先載入圖表數據' });
      return;
    }

    if (viewMode === 'single') {
      Modal.warning({ 
        title: '請切換到多圖模式', 
        content: '批次下載 PDF 需要在「多重圖表」模式下進行，請先切換模式並勾選要下載的感測器。' 
      });
      return;
    }

    if (selectedSensors.length === 0) {
      Modal.warning({ title: '請選擇感測器', content: '請至少勾選一個感測器' });
      return;
    }

    setShowDownloadModal(false);
    const loadingModal = Modal.info({ 
      title: '正在生成 PDF', 
      content: '請稍候...', 
      okButtonProps: { style: { display: 'none' } } 
    });

    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = 297;
      const pageHeight = 210;
      const margin = 10;
      const chartWidth = pageWidth - 2 * margin;
      const chartHeight = pageHeight - 2 * margin - 15;
      
      // 下載所有選中的感測器
      for (let i = 0; i < selectedSensors.length; i++) {
        const sensorKey = selectedSensors[i];
        const sensor = activeSensors.find(s => s.key === sensorKey);
        
        if (!sensor) continue;
        
        loadingModal.update({
          content: `正在處理 ${sensor.name} (${i + 1}/${selectedSensors.length})...`
        });
        
        const chartElement = document.querySelector(`#chart-${sensorKey} .recharts-wrapper`);
        
        if (!chartElement) continue;
        
        const canvas = await html2canvas(chartElement, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        if (i > 0) pdf.addPage();
        
        // 使用 Canvas 繪製標題（支援中文）
        const titleCanvas = document.createElement('canvas');
        titleCanvas.width = 1200;
        titleCanvas.height = 80;
        const ctx = titleCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 1200, 80);
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 32px Microsoft JhengHei, PingFang TC, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(sensor.name + ' 監測圖表', 600, 50);
        
        const titleImg = titleCanvas.toDataURL('image/png');
        pdf.addImage(titleImg, 'PNG', margin, 5, chartWidth, 10);
        pdf.addImage(imgData, 'PNG', margin, 20, chartWidth, chartHeight);
      }
      
      const fileName = `氣體監測圖表_${id}_${new Date().toLocaleDateString('zh-TW').replace(/\//g, '')}.pdf`;
      pdf.save(fileName);
      
      Modal.destroyAll();
      Modal.success({ 
        title: 'PDF 生成成功', 
        content: `已匯出 ${selectedSensors.length} 個感測器圖表` 
      });
      
    } catch (error) {
      console.error('生成 PDF 失敗:', error);
      Modal.destroyAll();
      Modal.error({ title: '生成失敗', content: error.message || '無法生成 PDF 文件' });
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      // 確保使用本地時間
      const startDate = dates[0].toDate();
      const endDate = dates[1].toDate();
      
      // 設置為當天的開始和結束時間
      const startOfDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endOfDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999);
      
      console.log('用戶選擇日期範圍:', {
        original: [dates[0].format(), dates[1].format()],
        processed: [startOfDay.toISOString(), endOfDay.toISOString()],
        local: [startOfDay.toLocaleString(), endOfDay.toLocaleString()]
      });
      
      setDateRange([startOfDay, endOfDay]);
    }
  };

  const zoom = () => {
    if (refLeft === refRight || refLeft === "" || refRight === "") return;

    let [Left, Right] = refLeft <= refRight ? [refLeft, refRight] : [refRight, refLeft];

    setState(state => ({
      ...state,
      left: Left,
      right: Right,
    }));
    setRight("");
    setLeft("");
  };

  const zoomOut = () => {
    setState(prevState => ({
      ...prevState,
      left: "dataMin",
      right: "dataMax",
      animation: true
    }));
    setLeft("");
    setRight("");
  };

  const TimeFormat = (num) => {
    const { left, right } = state;
    const defaultFormat = new Date(num).toLocaleDateString();
    if (left === "dataMin" || right === "dataMax") return defaultFormat;
    if (right - left > 24 * 60 * 60 * 1000) return defaultFormat;
    return new Date(num).toLocaleString();
  };

  const calculateYDomain = (sensorKey, data) => {
    const config = sensorConfigs[sensorKey];
    const dataKey = SENSOR_MAPPING[sensorKey].dataKey;
    const values = data.map(item => item[dataKey]).filter(val => val !== null && val !== undefined && !isNaN(val));
    const minValue = values.length > 0 ? Math.min(...values) : null;
    const maxValue = values.length > 0 ? Math.max(...values) : null;

    // 熱指數：虛線開啟時 Y 軸需涵蓋 32.2~54.4 才看得到四個區塊
    if (sensorKey === HEAT_SENSOR_KEY) {
      if (isHeatLinesEnabled(config)) {
        const lowest = HEAT_LEVEL_LINES[0].value;
        const highest = HEAT_LEVEL_LINES[HEAT_LEVEL_LINES.length - 1].value;
        return [
          Math.floor(Math.min(lowest - 6, minValue ?? Infinity) - 1),
          Math.ceil(Math.max(highest + 4, maxValue ?? -Infinity) + 1)
        ];
      }
      return values.length > 0 ? [Math.floor(minValue - 2), Math.ceil(maxValue + 2)] : ['auto', 'auto'];
    }

    // 只把有啟用的上限／下限納入 Y 軸範圍，停用的一側不再撐開圖表
    const upperOn = isUpperEnabled(config);
    const lowerOn = isLowerEnabled(config);
    const tolerance = lowerOn ? (Number(config.bounds.tolerance) || 0) : 0;

    const candidatesMin = [];
    const candidatesMax = [];
    if (values.length > 0) {
      const pad = Math.max((maxValue - minValue) * 0.1, 1);
      candidatesMin.push(minValue - pad);
      candidatesMax.push(maxValue + pad);
    }
    if (lowerOn) {
      candidatesMin.push(config.bounds.lowerBound - tolerance - 10);
      candidatesMax.push(config.bounds.lowerBound + 5);
    }
    if (upperOn) {
      candidatesMax.push(config.bounds.upperBound + 20);
      candidatesMin.push(config.bounds.upperBound - 5);
    }

    if (candidatesMin.length === 0) return ['auto', 'auto'];
    return [Math.min(...candidatesMin), Math.max(...candidatesMax)];
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === "single" && selectedSensors.length > 0) {
      setActiveSensor(selectedSensors[0]);
    }
  };

  const handleSensorSelectionChange = (checkedValues) => {
    setSelectedSensors(checkedValues);
    if (checkedValues.length > 0) {
      setActiveSensor(checkedValues[0]);
    }
  };

  // 雨量：整點切分的時雨量長條圖（進行中的小時以淡色標示「統計中」）
  const renderRainBarChart = (sensorKey, sensor, height = 400) => {
    const dataKey = SENSOR_MAPPING[sensorKey].dataKey;
    const hourlyData = aggregateRainHourly(data, dataKey);
    // 上限停用時不畫時雨量上限線、Tooltip 也不判斷超限
    const upperBound = isUpperEnabled(sensorConfigs[sensorKey])
      ? sensorConfigs[sensorKey].bounds.upperBound
      : undefined;

    const maxVal = hourlyData.length > 0 ? Math.max(...hourlyData.map(h => h.value)) : 0;
    const yMax = Math.max(Number(upperBound) || 0, maxVal) * 1.2 || 10;

    return (
      <>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={hourlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, parseFloat(yMax.toFixed(1))]}
              width={40}
            />
            <Tooltip
              labelFormatter={(label) => `${label} 起一小時`}
              formatter={(value, name, props) => {
                const suffix = props?.payload?.inProgress ? '（本小時統計中）' : '';
                let status = "";
                if (upperBound !== undefined && upperBound !== null && value > upperBound) {
                  status = " (超出時雨量上限)";
                }
                return [`${value} mm${status}${suffix}`, sensor.name];
              }}
            />
            <Legend />
            {upperBound !== undefined && upperBound !== null && (
              <ReferenceLine
                y={upperBound}
                label="時雨量上限"
                stroke="red"
                strokeDasharray="2 2"
              />
            )}
            <Bar dataKey="value" name={`${sensor.name}（時雨量 mm）`} fill={sensor.color}>
              {hourlyData.map((entry, index) => (
                <Cell key={index} fillOpacity={entry.inProgress ? 0.4 : 1} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ textAlign: 'right', fontSize: 12, color: '#999', marginTop: 4 }}>
          時雨量按整點切分；淡色長條為進行中的小時（統計中）
        </div>
      </>
    );
  };

  const activeSensors = getActiveSensors();
  const currentSensor = activeSensors.find(s => s.key === activeSensor);

  if (activeSensors.length === 0) {
    return (
      <Modal
        title="感測器圖表"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVisible(false)}>
            關閉
          </Button>
        ]}
      >
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <p>沒有已配置的感測器</p>
          <p>請先在設定中為感測器命名</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="感測器數據圖表"
      open={visible}
      onCancel={() => setVisible(false)}
      width="95%"
      style={{ top: 20 }}
      footer={null}
    >
      {/* 控制區域 */}
      <div style={{ marginBottom: 16, padding: "16px 0", borderBottom: "1px solid #f0f0f0" }}>
        <Row gutter={16} align="middle">
          <Col>
            <Space align="center">
              <span>時間範圍：</span>
              <RangePicker
                showTime
                value={dateRange.length === 2 ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : []}
                onChange={handleDateRangeChange}
              />
            </Space>
          </Col>
          <Col>
            <Space align="center">
              <span>檢視模式：</span>
              <Select value={viewMode} onChange={handleViewModeChange} style={{ width: 120 }}>
                <Option value="single">單一圖表</Option>
                <Option value="multiple">多重圖表</Option>
              </Select>
            </Space>
          </Col>
        </Row>
        
        <Row gutter={16} align="middle" style={{ marginTop: 8 }}>
          <Col flex={1}>
            {viewMode === "single" ? (
              <Space align="center">
                <span>選擇感測器：</span>
                <Select 
                  value={activeSensor} 
                  onChange={setActiveSensor} 
                  style={{ width: 200 }}
                >
                  {activeSensors.map(sensor => (
                    <Option key={sensor.key} value={sensor.key}>
                      {sensor.name}
                    </Option>
                  ))}
                </Select>
              </Space>
            ) : (
              <Space align="center">
                <span>選擇感測器：</span>
                <Checkbox.Group 
                  value={selectedSensors}
                  onChange={handleSensorSelectionChange}
                >
                  <Row>
                    {activeSensors.map(sensor => (
                      <Col key={sensor.key} style={{ marginRight: 16 }}>
                        <Checkbox value={sensor.key}>
                          {sensor.name}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Space>
            )}
          </Col>
          <Col>
            <Space>
              <span>智能補點：</span>
              <Switch 
                checked={enableInterpolation} 
                onChange={setEnableInterpolation}
                loading={loading}
              />
              <Button onClick={zoomOut}>重置縮放</Button>
              <Button onClick={loadChartData} loading={loading}>
                刷新數據
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={() => setShowDownloadModal(true)}
                disabled={!data || data.length === 0}
              >
                下載資料
              </Button>
            </Space>
          </Col>
        </Row>
        
      </div>

      {/* 圖表顯示區域 */}
      {viewMode === "single" && currentSensor ? (
        // 單一圖表模式
        <div id="single-chart-container">
          <Card title={currentSensor.isRain ? `${currentSensor.name} 時雨量圖表（整點）` : `${currentSensor.name} 監測圖表`} style={{ marginBottom: 16 }}>
            {currentSensor.isRain ? (
              renderRainBarChart(activeSensor, currentSensor, 400)
            ) : (
            <ResponsiveContainer
              width="100%" 
              height={400}
              style={{ 
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
            >
              <LineChart
                data={data}
                margin={0}
                onMouseDown={(e) => {
                  if (e) {
                      setLeft(e.activeLabel);
                      setRight("");
                  }
                }}
                onMouseMove={(e) => {
                  if (e && refLeft !== "") {
                    setRight(e.activeLabel);
                  }
                }}
                onMouseUp={(e) => {
                  if (refLeft !== "" && refRight !== "" && refLeft !== refRight) {
                    zoom();
                  } else {
                    setLeft("");
                    setRight("");
                  }
                }}
              >
                {renderReferenceLines(activeSensor, sensorConfigs[activeSensor], true)}
                <XAxis
                  allowDataOverflow
                  dataKey="time"
                  domain={[left, right]}
                  type="number"
                  tickFormatter={TimeFormat}
                  tickCount={10}
                />
                <YAxis
                  allowDataOverflow
                  domain={calculateYDomain(activeSensor, data)}
                  width={40}
                />
                <Tooltip
                  labelFormatter={(e) => new Date(e).toLocaleString()}
                  formatter={(value, name, props) => {
                    const displayValue = value === null || value === undefined ? "--" : value;
                    const status = getValueStatus(activeSensor, sensorConfigs[activeSensor], value);

                    const calibration = sensorConfigs[activeSensor]?.calibration;
                    let calibrationInfo = "";
                    if (calibration && (calibration.A !== 1 || calibration.B !== 0)) {
                      calibrationInfo = ` [已校正: A=${calibration.A}, B=${calibration.B}]`;
                    }
                    
                    return [displayValue + status + calibrationInfo, currentSensor.name];
                  }}
                />
                <Legend />
                <Line
                  type="linear"
                  dataKey={SENSOR_MAPPING[activeSensor].dataKey}
                  name={currentSensor.name}
                  stroke={currentSensor.color}
                  dot={false}
                  connectNulls={enableInterpolation} // 根據補點設置決定是否連接null值
                />
                {refLeft && refRight && (
                  <ReferenceArea
                    x1={refLeft}
                    x2={refRight}
                    strokeOpacity={0.3}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
            )}
          </Card>
        </div>
      ) : (
        // 多重圖表模式
        <div>
          <Row gutter={[16, 16]}>
            {selectedSensors.map(sensorKey => {
              const sensor = activeSensors.find(s => s.key === sensorKey);
              if (!sensor) return null;

              return (
                <Col span={12} key={sensorKey}>
                  <Card id={`chart-${sensorKey}`} title={sensor.isRain ? `${sensor.name}（時雨量）` : sensor.name} size="small">
                    {sensor.isRain ? (
                      renderRainBarChart(sensorKey, sensor, 250)
                    ) : (
                    <ResponsiveContainer
                      width="100%" 
                      height={250}
                      style={{ 
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none'
                      }}
                    >
                      <LineChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <XAxis
                          dataKey="time"
                          tickFormatter={TimeFormat}
                          tickCount={5}
                        />
                        <YAxis
                          domain={calculateYDomain(sensorKey, data)}
                          width={40}
                        />
                        <Tooltip
                          labelFormatter={(e) => new Date(e).toLocaleString()}
                          formatter={(value, name, props) => {
                            // 使用閉包中的 sensorKey 和 sensor
                            const currentSensorKey = sensorKey;
                            const currentSensor = sensor;
                            
                            const displayValue = value === null || value === undefined ? "--" : value;
                            const status = getValueStatus(currentSensorKey, sensorConfigs[currentSensorKey], value);

                            const calibration = sensorConfigs[currentSensorKey]?.calibration;
                            let calibrationInfo = "";
                            if (calibration && (calibration.A !== 1 || calibration.B !== 0)) {
                              calibrationInfo = ` [已校正: A=${calibration.A}, B=${calibration.B}]`;
                            }
                            
                            return [displayValue + status + calibrationInfo, currentSensor.name];
                          }}
                        />
                        <Line
                          type="linear"
                          dataKey={SENSOR_MAPPING[sensorKey].dataKey}
                          name={sensor.name}
                          stroke={sensor.color}
                          dot={false}
                          connectNulls={enableInterpolation}
                        />
                        {renderReferenceLines(sensorKey, sensorConfigs[sensorKey], false)}
                      </LineChart>
                    </ResponsiveContainer>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
      
      {/* 下載選擇 Modal */}
      <Modal
        title="選擇下載格式"
        open={showDownloadModal}
        onCancel={() => setShowDownloadModal(false)}
        footer={null}
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Button 
            block 
            size="large"
            icon={<DownloadOutlined />}
            onClick={exportToCSV}
          >
            下載 CSV 檔案
          </Button>
          <Button 
            block 
            size="large"
            icon={<DownloadOutlined />}
            onClick={exportToPDF}
          >
            下載 PDF 圖表
          </Button>
        </Space>
      </Modal>
    </Modal>
  );
}